import { NavLink, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import UserProfile from '@/components/User/UserProfile'
import { UserRole } from '@/constants/room'
import NotificationIcon from '@/components/Notification/NotificationIcon'
import {
  Settings,
  Monitor,
  BarChart3,
  Activity,
  ListChecks,
  FileText,
  GraduationCap,
} from 'lucide-react'
import ROUTES from '@/routes/routes'
import { cn } from '@/lib/utils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { RootState } from '@/redux/store'

const Navbar: React.FC = () => {
  const location = useLocation()
  const pathname = location.pathname
  const isSpecificRoom = pathname.startsWith('/rooms/')
  const isAdjudication = pathname.includes('/adjudicate')
  const isSettings = pathname.includes('/settings')
  const isConfiguration = pathname.includes('/configuration')
  const isConfigure = pathname.includes('/configure')
  const isQuickControls = pathname.includes('/quick-controls')
  const isMonitoring = pathname.includes('/monitoring') && !pathname.includes('/charter-monitoring')
  const isGameAnalysis = pathname.includes('/game-analysis')
  const isGameSummary = pathname.includes('/game-summary')
  const isGameSetupWizard = pathname.includes('/game-setup-wizard')
  const isCharterMonitoring = pathname.includes('/charter-monitoring')
  const isCharterSubPage =
    pathname.includes('/charter-monitoring/prep') ||
    pathname.includes('/charter-monitoring/group')
  // Charter Classes live in their own tree and have no game, so none of the
  // room-scoped game links above apply to them.
  const isCharterClasses = pathname.startsWith('/charter-classes')
  const params = useParams()
  const roomId = params.roomId

  const { user, isSimulationMode } = useEffectiveUser()
  const charterEnabled = useSelector(
    (state: RootState) => state.room.currentRoom?.charter_enabled === true
  )
  const isCharterOnlyRoom = useSelector(
    (state: RootState) => state.room.currentRoom?.is_charter_only === true
  )

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isSimulationMode) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  if (user?.role === UserRole.Student) {
    return (
      <div className='py-4 px-14'>
        <div className='w-full h-[68px] bg-[#E6ECF2] rounded-full px-6 flex items-center justify-between'>
          <div className='flex items-center gap-1'>
            <NavLink
              to={ROUTES.STUDENT.DASHBOARD}
              className={cn(
                'flex-shrink-0',
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
              onClick={handleNavClick}
            >
              <img
                src='/images/oge-logo.svg'
                alt='OGE-Logo'
                className='h-[2.13rem] w-[3.25rem]'
              />
            </NavLink>

            <NavLink
              to={ROUTES.STUDENT.DASHBOARD}
              end
              className={({ isActive }) =>
                cn(
                  'rounded-full hover:bg-[#02357217] transition-colors',
                  isActive && 'bg-[#02357217] font-bold',
                  isSimulationMode &&
                    'pointer-events-none opacity-50 cursor-not-allowed'
                )
              }
              onClick={handleNavClick}
            >
              <div className='h-11 px-[0.63rem] flex items-center justify-center gap-2'>
                <img
                  src='/icons/home-building.svg'
                  alt='Building'
                  className='h-[1.13rem] w-4'
                />
                <span className='text-base text-[#023572] font-medium'>
                  Dashboard
                </span>
              </div>
            </NavLink>
          </div>

          <div className='flex items-center gap-4'>
            <div
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <NotificationIcon />
            </div>
            <div
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <UserProfile />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isProfessorPlayerView = pathname.match(
    /prof-dashboard\/[A-Z0-9]{10}\/player|rooms\/[A-Z0-9]+\/charter-monitoring\/(prep|group)\//
  )
  const isCharterMonitorView = pathname.match(
    /rooms\/[A-Z0-9]+\/charter-monitoring\/(prep|group)\//
  )

  if (isProfessorPlayerView && roomId) {
    return (
      <div className='py-4 px-14'>
        <div className='w-full h-[68px] bg-[#E6ECF2] rounded-full px-6 flex items-center justify-between'>
          <div className='flex items-center h-full gap-2'>
            <NavLink
              to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
              onClick={handleNavClick}
            >
              <img
                src='/images/oge-logo.svg'
                alt='OGE-Logo'
                className='h-[2.13rem] w-[3.25rem]'
              />
            </NavLink>

            <NavLink
              to={`/rooms/${roomId}`}
              className={cn(
                'rounded-full hover:bg-[#02357217]',
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
              onClick={handleNavClick}
            >
              <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                <img
                  src='/icons/home-building.svg'
                  alt='Building'
                  className='h-[1.13rem] w-[1.13rem]'
                />
                <span className='text-base text-[#023572]'>Game Room</span>
              </div>
            </NavLink>

            {isCharterMonitorView ? (
              <NavLink
                to={ROUTES.PROFESSOR.ROOM_CHARTER_MONITORING(roomId)}
                className={({ isActive }) =>
                  cn(
                    'rounded-full hover:bg-[#02357217]',
                    (isActive || isCharterMonitoring) &&
                      !isCharterSubPage &&
                      'bg-[#02357217] font-bold',
                    isSimulationMode &&
                      'pointer-events-none opacity-50 cursor-not-allowed'
                  )
                }
                onClick={handleNavClick}
              >
                <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                  <FileText className='h-[1.13rem] w-[1.13rem] text-[#023572]' />
                  <span className='text-base text-[#023572]'>Charter</span>
                </div>
              </NavLink>
            ) : (
              <NavLink
                to={ROUTES.PROFESSOR.MONITORING(roomId)}
                className={({ isActive }) =>
                  cn(
                    'rounded-full hover:bg-[#02357217]',
                    (isActive || isMonitoring) && 'bg-[#02357217] font-bold',
                    isSimulationMode &&
                      'pointer-events-none opacity-50 cursor-not-allowed'
                  )
                }
                onClick={handleNavClick}
              >
                <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                  <Monitor className='h-[1.13rem] w-[1.13rem] text-[#023572]' />
                  <span className='text-base text-[#023572]'>Monitoring</span>
                </div>
              </NavLink>
            )}
          </div>
          <div className='flex items-center justify-between w-[6.75rem] h-16 px-3'>
            <NotificationIcon />
            <UserProfile />
          </div>
        </div>
      </div>
    )
  }

  // In a room the bar holds every game link; below 1280px only the icons fit.
  const labelClass = cn(
    'text-base text-[#023572]',
    isSpecificRoom && roomId && !isCharterOnlyRoom && 'hidden xl:inline'
  )

  return (
    <>
      <div className='py-4 px-4 lg:px-14'>
        <div className='w-full h-[68px] bg-[#E6ECF2] rounded-full px-6 flex items-center justify-between'>
          <div className='flex items-center h-full gap-1 xl:gap-5'>
            <NavLink
              to={ROUTES.PROFESSOR.ROOMS}
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
              onClick={handleNavClick}
            >
              <img
                src='/images/oge-logo.svg'
                alt='OGE-Logo'
                className='h-[2.13rem] w-[3.25rem]'
              />
            </NavLink>

            <NavLink
              title='Dashboard'
              aria-label='Dashboard'
              to={ROUTES.PROFESSOR.ROOMS}
              end
              className={({ isActive }) =>
                cn(
                  'rounded-full',
                  isActive && !isSpecificRoom && 'bg-[#02357217] font-bold',
                  isSimulationMode &&
                    'pointer-events-none opacity-50 cursor-not-allowed'
                )
              }
              onClick={handleNavClick}
            >
              <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                <img
                  src='/icons/home-building.svg'
                  alt='Building'
                  className='h-[1.31rem] w-[1.31rem]'
                />
                <span className={labelClass}>Dashboard</span>
              </div>
            </NavLink>

            <NavLink
              title='Charter Classes'
              aria-label='Charter Classes'
              to={ROUTES.PROFESSOR.CHARTER_CLASSES}
              className={() =>
                cn(
                  'rounded-full',
                  isCharterClasses && 'bg-[#02357217] font-bold',
                  isSimulationMode &&
                    'pointer-events-none opacity-50 cursor-not-allowed'
                )
              }
              onClick={handleNavClick}
            >
              <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                <GraduationCap className='h-[1.31rem] w-[1.31rem] text-[#023572]' />
                <span className={labelClass}>
                  Charter Classes
                </span>
              </div>
            </NavLink>

            {/* Room-scoped game links. Suppressed for a Charter Class, which
                has no rounds, timer, adjudication or game analysis. */}
            {isSpecificRoom && roomId && !isCharterOnlyRoom && (
              <>
                <NavLink
                  title='Game Control Panel'
                  aria-label='Game Control Panel'
                  to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      isActive &&
                        !isAdjudication &&
                        !isSettings &&
                        !isConfiguration &&
                        !isConfigure &&
                        !isGameSetupWizard &&
                        !isQuickControls &&
                        !isMonitoring &&
                        !isCharterMonitoring &&
                        !isGameAnalysis &&
                        !isGameSummary &&
                        'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <img
                      src='/icons/room-building.svg'
                      alt='Building'
                      className='h-[1.13rem] w-4'
                    />
                    <span className={labelClass}>
                      Game Control Panel
                    </span>
                  </div>
                </NavLink>
                <NavLink
                  title='Game Setup'
                  aria-label='Game Setup'
                  to={ROUTES.PROFESSOR.GAME_SETUP_WIZARD(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      (isActive || isGameSetupWizard) &&
                        'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <ListChecks className='h-[1.13rem] w-4 text-[#023572]' />
                    <span className={labelClass}>
                      Game Setup
                    </span>
                  </div>
                </NavLink>
                <NavLink
                  title='Quick Controls'
                  aria-label='Quick Controls'
                  to={ROUTES.PROFESSOR.QUICK_CONTROLS(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      (isActive || isQuickControls) &&
                        'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <Settings className='h-[1.13rem] w-4 text-[#023572]' />
                    <span className={labelClass}>
                      Quick Controls
                    </span>
                  </div>
                </NavLink>

                <NavLink
                  title='Monitoring'
                  aria-label='Monitoring'
                  to={ROUTES.PROFESSOR.MONITORING(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      (isActive || isMonitoring) && 'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <Monitor className='h-[1.13rem] w-4 text-[#023572]' />
                    <span className={labelClass}>Monitoring</span>
                  </div>
                </NavLink>

                {charterEnabled && (
                  <NavLink
                    title='Charter'
                    aria-label='Charter'
                    to={ROUTES.PROFESSOR.ROOM_CHARTER_MONITORING(roomId)}
                    className={({ isActive }) =>
                      cn(
                        'rounded-full',
                        (isActive || isCharterMonitoring) &&
                          !isCharterSubPage &&
                          'bg-[#02357217] font-bold',
                        isSimulationMode &&
                          'pointer-events-none opacity-50 cursor-not-allowed'
                      )
                    }
                    onClick={handleNavClick}
                  >
                    <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                      <FileText className='h-[1.13rem] w-4 text-[#023572]' />
                      <span className={labelClass}>Charter</span>
                    </div>
                  </NavLink>
                )}

                <NavLink
                  title='Game Analysis'
                  aria-label='Game Analysis'
                  to={ROUTES.PROFESSOR.GAME_ANALYSIS(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      (isActive || isGameAnalysis) &&
                        'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <BarChart3 className='h-[1.13rem] w-4 text-[#023572]' />
                    <span className={labelClass}>
                      Game Analysis
                    </span>
                  </div>
                </NavLink>

                <NavLink
                  title='Game Summary'
                  aria-label='Game Summary'
                  to={ROUTES.PROFESSOR.GAME_SUMMARY(roomId)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full',
                      (isActive || isGameSummary) &&
                        'bg-[#02357217] font-bold',
                      isSimulationMode &&
                        'pointer-events-none opacity-50 cursor-not-allowed'
                    )
                  }
                  onClick={handleNavClick}
                >
                  <div className='h-11 p-[0.63rem] flex items-center justify-center gap-2'>
                    <Activity className='h-[1.13rem] w-4 text-[#023572]' />
                    <span className={labelClass}>
                      Game Summary
                    </span>
                  </div>
                </NavLink>
              </>
            )}
          </div>
          <div className='flex items-center justify-between w-[6.75rem] h-16 px-3'>
            <div
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <NotificationIcon />
            </div>
            <div
              className={cn(
                isSimulationMode &&
                  'pointer-events-none opacity-50 cursor-not-allowed'
              )}
            >
              <UserProfile />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Navbar
